import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Check, X, Upload, Download } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TYPE_LABEL = {
  asset: "Aset", contra_asset: "Kontra Aset", liability: "Liabilitas",
  equity: "Ekuitas", revenue: "Pendapatan", cogs: "HPP", expense: "Beban",
};
const TYPE_COLOR = {
  asset: "bg-blue-50 text-blue-700 border-blue-200",
  contra_asset: "bg-slate-100 text-slate-700 border-slate-200",
  liability: "bg-amber-50 text-amber-700 border-amber-200",
  equity: "bg-violet-50 text-violet-700 border-violet-200",
  revenue: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cogs: "bg-orange-50 text-orange-700 border-orange-200",
  expense: "bg-rose-50 text-rose-700 border-rose-200",
};

const COAPage = () => {
  const { user } = useAuth();
  const [coa, setCoa] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "expense", subtype: "" });

  const load = () => api.get("/coa").then((r) => setCoa(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (a) => { setEditing(a.code); setEditName(a.name); setEditCode(a.code); };
  const saveEdit = async (code) => {
    try {
      await api.put(`/coa/${code}`, { name: editName, new_code: editCode });
      toast.success("Akun disimpan"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/coa", form);
      toast.success("Akun dibuat"); setOpen(false); load();
      setForm({ code: "", name: "", type: "expense", subtype: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const suggestCode = async (t) => {
    try {
      const r = await api.get(`/coa/next-code?type=${t}`);
      setForm((f) => ({ ...f, type: t, code: r.data.next_code }));
    } catch (e) { setForm((f) => ({ ...f, type: t })); }
  };

  const del = async (code) => {
    if (!window.confirm("Hapus akun kustom ini?")) return;
    try { await api.delete(`/coa/${code}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const grouped = coa.reduce((acc, c) => {
    (acc[c.type] = acc[c.type] || []).push(c); return acc;
  }, {});
  const order = ["asset", "contra_asset", "liability", "equity", "revenue", "cogs", "expense"];

  return (
    <div className="space-y-6" data-testid="coa-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" data-testid="coa-title">Chart of Accounts (COA)</h1>
          <p className="text-sm text-slate-500 mt-1">Standar SAK ETAP — nama akun dapat diedit oleh admin</p>
        </div>
        {user?.role === "admin" && (
          <div className="flex gap-2">
            <Button data-testid="coa-template-button" variant="ghost" size="sm" onClick={() => {
              const token = localStorage.getItem("spbu_token");
              fetch(`${API}/import/coa-template.xlsx`, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.blob()).then(b => {
                  const url = URL.createObjectURL(b);
                  const a = document.createElement("a"); a.href = url; a.download = "coa_template.xlsx"; a.click();
                });
            }}><Download className="w-4 h-4 mr-2" />Template</Button>
            <label className="inline-flex">
              <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="coa-import-input"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  const token = localStorage.getItem("spbu_token");
                  try {
                    const res = await fetch(`${API}/import/coa.xlsx`, {
                      method: "POST", body: fd,
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!res.ok) throw new Error((await res.json()).detail || "Gagal");
                    const d = await res.json();
                    toast.success(`Import selesai — ${d.created} baru, ${d.updated} update${d.skipped?.length ? `, ${d.skipped.length} skip` : ""}`);
                    load();
                  } catch (err) { toast.error(err.message); }
                  e.target.value = "";
                }} />
              <Button asChild variant="outline"><span data-testid="coa-import-button" className="cursor-pointer"><Upload className="w-4 h-4 mr-2" />Import Excel</span></Button>
            </label>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="coa-add-button" className="bg-[#E30613] hover:bg-[#C8040F]"><Plus className="w-4 h-4 mr-2" />Akun Kustom</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Tambah Akun Baru</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div><Label>Tipe Akun</Label>
                    <Select value={form.type} onValueChange={suggestCode}>
                      <SelectTrigger data-testid="coa-form-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {order.map((t) => <SelectItem key={t} value={t} data-testid={`coa-form-type-${t}`}>{TYPE_LABEL[t]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Kode akun akan disarankan otomatis</p>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Kode Akun</Label><Input data-testid="coa-form-code" value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value }))} required placeholder="1110" /></div>
                    <div className="col-span-2"><Label>Nama Akun</Label><Input data-testid="coa-form-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Contoh: Bank BCA — 1234567890" /></div>
                  </div>
                  <DialogFooter><Button type="submit" data-testid="coa-form-submit" className="bg-[#E30613] hover:bg-[#C8040F]">Simpan</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {order.filter((t) => grouped[t]).map((t) => (
        <Card key={t} data-testid={`coa-group-${t}`}>
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold">{TYPE_LABEL[t]}</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-24">Kode</TableHead>
                <TableHead>Nama Akun</TableHead>
                <TableHead>Tipe</TableHead>
                {user?.role === "admin" && <TableHead className="w-32 text-right">Aksi</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {grouped[t].map((c) => (
                  <TableRow key={c.code} data-testid={`coa-row-${c.code}`}>
                    <TableCell className="font-mono-fig font-semibold">
                      {editing === c.code ? (
                        <Input data-testid={`coa-code-input-${c.code}`} value={editCode} onChange={(e) => setEditCode(e.target.value)} className="w-24 h-8 font-mono-fig" />
                      ) : c.code}
                    </TableCell>
                    <TableCell data-testid={`coa-name-${c.code}`}>
                      {editing === c.code ? (
                        <Input data-testid={`coa-name-input-${c.code}`} value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                      ) : c.name}
                      {c.custom && <Badge variant="secondary" className="ml-2 text-xs">custom</Badge>}
                    </TableCell>
                    <TableCell><Badge className={`${TYPE_COLOR[c.type]} border text-xs`}>{TYPE_LABEL[c.type]}</Badge></TableCell>
                    {user?.role === "admin" && (
                      <TableCell className="text-right">
                        {editing === c.code ? (
                          <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" data-testid={`coa-save-${c.code}`} onClick={() => saveEdit(c.code)}><Check className="w-4 h-4 text-emerald-600" /></Button>
                            <Button size="icon" variant="ghost" data-testid={`coa-cancel-${c.code}`} onClick={() => setEditing(null)}><X className="w-4 h-4 text-slate-500" /></Button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" data-testid={`coa-edit-${c.code}`} onClick={() => startEdit(c)}><Pencil className="w-4 h-4" /></Button>
                            {c.custom && <Button size="icon" variant="ghost" data-testid={`coa-delete-${c.code}`} onClick={() => del(c.code)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default COAPage;
